/**
 * MainLayout - Main application layout with tabbed navigation
 * Tabs at top, panels on left, visualization on right
 */

import { useState, useCallback, type ReactNode } from 'react';
import { ConnectionBar } from './ConnectionBar';

export interface TabConfig {
  id: string;
  label: string;
  icon?: string;
  panels: ReactNode;
}

export interface MainLayoutProps {
  tabs: TabConfig[];
  visualization: ReactNode;
  defaultTab?: string;
  toolbar?: ReactNode;
}

function readStoredTab(tabs: TabConfig[], defaultTab: string | undefined): string {
  try {
    const stored = localStorage.getItem('stak-activeTab');
    if (stored && tabs.some((t) => t.id === stored)) return stored;
  } catch {
    // localStorage unavailable
  }
  return defaultTab ?? tabs[0]?.id ?? '';
}

export function MainLayout({ tabs, visualization, defaultTab, toolbar }: MainLayoutProps) {
  const [activeTab, setActiveTabRaw] = useState(() => readStoredTab(tabs, defaultTab));

  const setActiveTab = useCallback((id: string) => {
    setActiveTabRaw(id);
    try {
      localStorage.setItem('stak-activeTab', id);
    } catch {
      // localStorage unavailable
    }
  }, []);

  const activePanels = tabs.find((tab) => tab.id === activeTab)?.panels;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f8f9fa',
      }}
    >
      {/* Header with tabs */}
      <header
        style={{
          backgroundColor: '#fff',
          borderBottom: '1px solid #dee2e6',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        {/* Title row */}
        <div
          style={{
            padding: '12px 24px 8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 600,
                color: '#e94560',
                letterSpacing: '0.5px',
              }}
            >
              STAK MIDI
            </h1>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6c757d' }}>
              STAK Isomorphic Keyboard Configurator
            </p>
          </div>
        </div>

        {/* Tab bar */}
        <nav
          style={{
            display: 'flex',
            gap: '2px',
            padding: '0 24px',
          }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === tab.id ? '#f8f9fa' : 'transparent',
                color: activeTab === tab.id ? '#e94560' : '#6c757d',
                fontWeight: activeTab === tab.id ? 600 : 400,
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                borderBottom: activeTab === tab.id ? '2px solid #e94560' : '2px solid transparent',
              }}
            >
              {tab.icon && <span style={{ fontSize: '16px' }}>{tab.icon}</span>}
              {tab.label}
            </button>
          ))}
        </nav>
      </header>

      {/* Connection status bar - always visible */}
      <ConnectionBar />

      {/* Main content */}
      <main
        style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: '360px 1fr',
          gap: '0',
          overflow: 'hidden',
        }}
      >
        {/* Left panel - configuration */}
        <aside
          style={{
            backgroundColor: '#fff',
            borderRight: '1px solid #dee2e6',
            overflow: 'auto',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {toolbar}
          {activePanels}
        </aside>

        {/* Right panel - visualization */}
        <section
          style={{
            overflow: 'auto',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            backgroundColor: '#f8f9fa',
          }}
        >
          {visualization}
        </section>
      </main>

      {/* Footer */}
      <footer
        style={{
          padding: '10px 16px',
          textAlign: 'center',
          fontSize: '11px',
          color: '#6c757d',
          borderTop: '1px solid #dee2e6',
          backgroundColor: '#fff',
          lineHeight: '1.6',
        }}
      >
        <div>STAK MIDI - Isomorphic MIDI Keyboard Prototype - Configurator</div>
        <div>
          Contact:{' '}
          <a href="mailto:cgpsmapper@gmail.com" style={{ color: '#e94560' }}>
            cgpsmapper@gmail.com
          </a>
          {' • '}v1.3
        </div>
      </footer>
    </div>
  );
}
